import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox } from '@ibsys/kit';
import type { ComboboxItem } from '@ibsys/kit';
import type { StrategistTargetAttribute } from '@/entities/strategist';
import { TargetMetricPicker } from '@/features/strategist-target';
import { ThresholdSlider } from '@/shared/ui';

/** Доступные значения контрола «сколько строк/столбцов показать». */
const TOP_VALUES = [10, 15, 20, 25, 30];

type Props = {
  /** Текущая целевая метрика */
  target: StrategistTargetAttribute;
  /** Смена целевой метрики */
  onTargetChange: (next: StrategistTargetAttribute) => void;
  /** Текущее число строк/столбцов */
  top: number;
  /** Смена числа строк/столбцов */
  onTopChange: (next: number) => void;
  /** Доступные пороги близости (шаги ползунков) */
  thresholds: number[];
  /** Текущий порог по возражениям (строки) */
  objectionThreshold: number | undefined;
  /** Смена порога по возражениям */
  onObjectionThresholdChange: (next: number) => void;
  /** Текущий порог по аргументам (столбцы) */
  argumentThreshold: number | undefined;
  /** Смена порога по аргументам */
  onArgumentThresholdChange: (next: number) => void;
};

/**
 * Панель контролов матрицы: выбор целевой метрики, ограничение числа
 * строк/столбцов (топ по частоте) и два независимых ползунка порога близости —
 * по возражениям (строки) и по аргументам (столбцы).
 */
export const StrategistMatrixControls: FC<Props> = ({
  target,
  onTargetChange,
  top,
  onTopChange,
  thresholds,
  objectionThreshold,
  onObjectionThresholdChange,
  argumentThreshold,
  onArgumentThresholdChange,
}) => {
  const { t } = useTranslation('strategist');

  const topOptions = useMemo<ComboboxItem[]>(() => TOP_VALUES.map((v) => ({ value: v, label: String(v) })), []);

  return (
    <div className="flex w-full flex-wrap items-end gap-4">
      <TargetMetricPicker value={target} onChange={onTargetChange} />
      <Combobox
        label={t('controls.top')}
        options={topOptions}
        value={top}
        onChange={(v) => onTopChange(Number(v ?? top))}
        isSearchable={false}
        className="min-w-32"
      />
      <ThresholdSlider
        label={t('matrix.thresholdObjection')}
        thresholds={thresholds}
        value={objectionThreshold}
        onChange={onObjectionThresholdChange}
        coarserLabel={t('threshold.coarser')}
        finerLabel={t('threshold.finer')}
      />
      <ThresholdSlider
        label={t('matrix.thresholdArgument')}
        thresholds={thresholds}
        value={argumentThreshold}
        onChange={onArgumentThresholdChange}
        coarserLabel={t('threshold.coarser')}
        finerLabel={t('threshold.finer')}
      />
    </div>
  );
};
